<script setup lang="ts">
import { SellingMethod } from '~/apiClient';
import { ROUTE_NAMES } from '~/constants/routes';

const { tt } = useTypedI18n();

const isOpen = ref(false);

const search = useHeaderSearch({
    onClose: () => {
        isOpen.value = false;
    },
});

const auctionItems = computed(() =>
    search.auctions.value.map(item => ({
        id: item.id,
        slug: item.slug,
        vin: item.mainAsset.vin,
        brand: item.mainAsset.brand,
        model: item.mainAsset.model,
        coverImage: item.mainAsset.coverImage,
        price: item.currentPrice,
    }))
);
const directSaleItems = computed(() =>
    search.directSales.value.map(item => ({
        id: item.id,
        slug: item.slug,
        vin: item.mainAsset.vin,
        brand: item.mainAsset.brand,
        model: item.mainAsset.model,
        coverImage: item.mainAsset.coverImage,
        price: item.buyNowPrice,
    }))
);
</script>

<template>
    <UModal v-model:open="isOpen" fullscreen :title="tt('search.aria.openSearch')" v-bind="modalNoDescAriaFixupProps">
        <UButton icon="lucide:search" size="xl" variant="ghost" color="neutral" class="lg:hidden" :aria-label="tt('search.aria.openSearch')" />
        <template #body>
            <div class="flex flex-col gap-3">
                <UInput
                    v-model="search.inputValue.value"
                    autofocus
                    leading-icon="lucide:search"
                    autocomplete="off"
                    :placeholder="tt('search.placeholder')"
                    size="lg"
                    @keydown.enter.prevent="search.onEnter()"
                >
                    <template #trailing>
                        <UButton v-if="search.inputValue.value" icon="lucide:x" size="xs" square variant="ghost" @click="search.onClear()" />
                    </template>
                </UInput>

                <div v-if="search.isFetching.value" class="flex justify-center py-8">
                    <UIcon name="lucide:loader-circle" class="animate-spin text-2xl text-neutral-400" />
                </div>

                <div v-else-if="search.isOpen.value && !search.hasResults.value" class="py-8 text-center text-neutral-500">
                    {{ tt('search.noResults', { q: search.debouncedValue.value.trim() }) }}
                </div>

                <template v-else-if="search.isOpen.value">
                    <HeaderSearchResultSection
                        class="mb-4"
                        title-key="search.section.auctions"
                        view-all-key="search.viewAll.auctions"
                        :items="auctionItems"
                        :total="search.auctionsTotal.value"
                        :route-name="ROUTE_NAMES.AUCTION_DETAIL"
                        :view-all-route="search.getViewAllRoute(SellingMethod.Auction, search.debouncedValue.value)"
                        @item-click="search.onResultClick()"
                    />
                    <HeaderSearchResultSection
                        title-key="search.section.buyNow"
                        view-all-key="search.viewAll.buyNow"
                        :items="directSaleItems"
                        :total="search.directSalesTotal.value"
                        :route-name="ROUTE_NAMES.DIRECT_SALE_DETAIL"
                        :view-all-route="search.getViewAllRoute(SellingMethod.DirectSale, search.debouncedValue.value)"
                        @item-click="search.onResultClick()"
                    />
                </template>
            </div>
        </template>
    </UModal>
</template>
